import { stringify } from "querystring";
import {
  Dispatch,
  KeyboardEvent,
  RefObject,
  SetStateAction,
  useRef,
} from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  Anchor,
  Icon,
  LineClamp,
  QuestionSVG,
  SearchSVG,
  type SvgComponentType,
} from "@newsela/angelou";
import AIToggle from "@/components/AIComponents/AIToggle";

import { ISuggestion } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchMenu.constants";
import {
  flexColumnCss,
  flexRowCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SearchSuggestionsPopout.styles";
import { getHighlightedSuggestionText } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SearchSuggestionsPopout.utils";
import {
  essentialQuestionTextCss,
  footerSectionCss,
  searchSuggestionAnchorCss,
  searchSuggestionIconCss,
  suggestionsUnorderedListCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SuggestionsList/SuggestionsList.styles";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useGetUserProperties from "@/users/v3/useGetUserProperties";

export const SEARCH_WITH_NEEDLE_URL = "/search?needle=";
export const UNIVERSAL_EMBED_SEARCH_WITH_NEEDLE_URL =
  "/search/universal-embed?needle=";

export const isQuestionSuggestion = (category: string) => category === "eq";

export const focusSuggestion = ({
  suggestionsListRef,
  index,
  increment,
}: {
  suggestionsListRef: RefObject<HTMLUListElement>;
  index: number;
  increment: boolean;
}) => {
  if (!suggestionsListRef.current) {
    return;
  }
  const suggestionAnchors = Array.from(
    suggestionsListRef.current.querySelectorAll("li > a"),
  );

  const targetIndex = increment ? index + 1 : index - 1;
  (suggestionAnchors[targetIndex] as HTMLAnchorElement)?.focus();
};

const SuggestionsList = ({
  inputValue,
  suggestions,
  setShowSuggestions,
  pressed,
  onToggle,
}: ISuggestionsList) => {
  const { pathname, push, query } = useRouter();
  const lunaToggleRef = useRef<HTMLDivElement>(null);
  const suggestionsListRef = useRef<HTMLUListElement>(null);

  const { canSeeAiFeatures } = useGetUserProperties();
  const [aiSmartSearchExperiment] =
    useGetUserExperimentSegment("ai_assisted_search");
  const isAiSmartSearchExperimentOn = aiSmartSearchExperiment === "on";
  const isUniversalEmbed = pathname.startsWith("/search/universal-embed");
  const canSeeAiSmartSearch =
    !isUniversalEmbed && canSeeAiFeatures && isAiSmartSearchExperimentOn;

  const onKeyDown = (
    event: KeyboardEvent<HTMLAnchorElement>,
    suggestion: string,
    index: number,
  ) => {
    if (event.key === "Escape") {
      setShowSuggestions(false);
    }
    if (event.key === "Enter") {
      setShowSuggestions(false);
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.set(LangstonParameterSchema.needle.key, suggestion);
      void push({
        pathname: isUniversalEmbed ? "/search/universal-embed" : "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    const isLastSuggestion = index === suggestions.length - 1;
    const isNotLastSuggestion = index < suggestions.length - 1;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (isLastSuggestion && canSeeAiSmartSearch) {
        lunaToggleRef.current?.focus();
      } else if (isNotLastSuggestion) {
        focusSuggestion({ suggestionsListRef, index, increment: true });
      }
    }
    const isNotFirstSuggestion = index >= 0;
    if (event.key === "ArrowUp" && isNotFirstSuggestion) {
      event.preventDefault();
      focusSuggestion({ suggestionsListRef, index, increment: false });
    }
  };

  const onLunaToggleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      setShowSuggestions(false);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (suggestionsListRef.current) {
        const suggestionAnchors = Array.from(
          suggestionsListRef.current.querySelectorAll("li > a"),
        );
        const lastSuggestionIndex = suggestions.length - 1;
        (suggestionAnchors[lastSuggestionIndex] as HTMLAnchorElement)?.focus();
      }
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <ul ref={suggestionsListRef} css={suggestionsUnorderedListCss}>
      {suggestions.map((item, index) => {
        let suggestion: string;
        let category: string;

        if (typeof item === "string") {
          suggestion = item;
          category = "";
        } else {
          suggestion = item.suggestion;
          category = item.category;
        }

        const isQuestion = isQuestionSuggestion(category);
        return (
          <li key={index}>
            <Link
              passHref
              legacyBehavior
              prefetch={false}
              href={encodeURI(
                `${isUniversalEmbed ? UNIVERSAL_EMBED_SEARCH_WITH_NEEDLE_URL : SEARCH_WITH_NEEDLE_URL}${suggestion}`,
              )}
            >
              <Anchor
                onKeyDown={(event) => onKeyDown(event, suggestion, index)}
                onClick={() => setShowSuggestions((show) => !show)}
                __cssFor={searchSuggestionAnchorCss}
                __classNameFor={{ root: "search-suggestion-anchor" }}
              >
                {isQuestion ? (
                  <Icon
                    SvgComponent={QuestionSVG as SvgComponentType}
                    __cssFor={searchSuggestionIconCss}
                  />
                ) : (
                  <Icon
                    SvgComponent={SearchSVG as SvgComponentType}
                    __cssFor={searchSuggestionIconCss}
                  />
                )}

                <div css={flexColumnCss}>
                  <div css={flexRowCss}>
                    {isQuestion ? (
                      <div data-testid="question-text">
                        <LineClamp lines={2} text={suggestion} />
                      </div>
                    ) : (
                      <div data-testid="highlighted-text">
                        {getHighlightedSuggestionText(suggestion, inputValue)}
                      </div>
                    )}
                  </div>

                  {isQuestionSuggestion(category) && (
                    <div css={essentialQuestionTextCss}>Essential question</div>
                  )}
                </div>
              </Anchor>
            </Link>
          </li>
        );
      })}
      {canSeeAiSmartSearch && (
        <div
          ref={lunaToggleRef}
          css={footerSectionCss}
          key={suggestions.length + 1}
          tabIndex={0}
          onKeyDown={onLunaToggleKeyDown}
        >
          <AIToggle
            pressed={pressed}
            onToggle={onToggle}
            ariaLabel="Search with Luna"
            labelText="Search with Luna"
            qaSelector="search-with-luna-toggle"
            description="Toggle Search with Luna"
          />
        </div>
      )}
    </ul>
  );
};

interface ISuggestionsList {
  inputValue: string;
  suggestions: ISuggestion[] | string[];
  setShowSuggestions: Dispatch<SetStateAction<boolean>>;
  onToggle: () => void;
  pressed: boolean;
}

SuggestionsList.displayName = "SuggestionsList";
export { SuggestionsList };
